/**
 * Preferences › Updates (desktop; settings pocket spec §6): automatic
 * updates, this build with Check now, and Restart to update once a download
 * is ready — the tray's install action. The second line says only what a
 * finished check established; manual checks also toast their progress.
 * Clicking the version five times shows the release channel switch and, on
 * Preview, the version picker.
 */
import { useState } from "react";
import { emit } from "@tauri-apps/api/event";
import {
  Button,
  PocketRow,
  PocketSection,
  ToggleSwitch,
} from "@/components/ui";
import {
  useDesktopUpdateStore,
  type DesktopUpdateCheck,
} from "@/desktop/desktop-update-store";
import { useAppVersion } from "@/desktop/use-app-version";
import { installStagedDesktopUpdate } from "@/desktop/use-auto-update";
import { useUpdateSettings } from "@/desktop/update-settings";
import { desktopVersionLabel } from "@/lib/desktop-version-label";
import { useT, type Translator } from "@/i18n";
import { settingsPreferencesMessages } from "@/i18n/messages/settings-preferences";
import { PreviewVersionRow } from "./preview-version-row";
import { ReleaseChannelRow } from "./release-channel-row";

const CHANNEL_ROW_CLICKS = 5;

function versionLine(
  t: Translator<typeof settingsPreferencesMessages.en>,
  lastCheck: DesktopUpdateCheck | null,
  readyVersion: string | null,
): string | undefined {
  const available =
    readyVersion ??
    (lastCheck?.status === "available" ? lastCheck.version : null);
  if (available)
    return t("versionAvailable", { version: desktopVersionLabel(available) });
  return lastCheck?.status === "up-to-date" ? t("upToDate") : undefined;
}

export function UpdatesPage() {
  const t = useT(settingsPreferencesMessages);
  const autoUpdate = useUpdateSettings((s) => s.autoUpdate);
  const setAutoUpdate = useUpdateSettings((s) => s.setAutoUpdate);
  const version = useAppVersion();
  const readyVersion = useDesktopUpdateStore((s) => s.readyVersion);
  const installing = useDesktopUpdateStore((s) => s.isInstalling);
  const lastCheck = useDesktopUpdateStore((s) => s.lastCheck);
  const [versionClicks, setVersionClicks] = useState(0);
  const countVersionClick = () => setVersionClicks((count) => count + 1);

  return (
    <PocketSection testId="settings-page-preferences-updates">
      <PocketRow
        testId="settings-row-updates-auto"
        title={t("updateAutomatically")}
        trailing={
          <ToggleSwitch
            checked={autoUpdate}
            onCheckedChange={setAutoUpdate}
            aria-label={t("updateAutomatically")}
            data-testid="updates-auto-toggle"
          />
        }
      />
      <PocketRow
        testId="settings-row-updates-version"
        title={
          version ? (
            <span
              role="button"
              tabIndex={0}
              title={t("build", { version })}
              onClick={countVersionClick}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                countVersionClick();
              }}
              data-testid="updates-version-label"
            >
              {desktopVersionLabel(version)}
            </span>
          ) : (
            "Denker"
          )
        }
        secondLine={versionLine(t, lastCheck, readyVersion)}
        trailing={
          readyVersion ? (
            <Button
              size="sm"
              disabled={installing}
              onClick={() => void installStagedDesktopUpdate()}
              data-testid="updates-restart"
            >
              {t("restartToUpdate")}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={lastCheck?.status === "checking"}
              onClick={() => void emit("check-for-update").catch(() => {})}
              data-testid="updates-check-now"
            >
              {t("checkNow")}
            </Button>
          )
        }
      />
      {versionClicks >= CHANNEL_ROW_CLICKS && (
        <>
          <ReleaseChannelRow />
          <PreviewVersionRow />
        </>
      )}
    </PocketSection>
  );
}
